import { useTranslation } from "react-i18next";

type HeatmapProps = {
  data: number[];
  cols?: number;
};

const BUCKETS = 4;

function bucketize(value: number, max: number): number {
  if (max === 0 || value === 0) return 0;
  const ratio = Math.min(1, value / max);
  return Math.max(1, Math.ceil(ratio * (BUCKETS - 1)));
}

const BUCKET_OPACITY = [0.08, 0.32, 0.55, 0.95];

export function Heatmap({ data, cols = 7 }: HeatmapProps) {
  const { t } = useTranslation("dashboard");
  const max = data.reduce((acc, v) => (v > acc ? v : acc), 0);
  const total = data.reduce((acc, v) => acc + v, 0);
  const rows = Math.ceil(data.length / cols);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between text-[12px] text-dim">
        <span>{t("heatmap.start", { defaultValue: "4 weeks ago" })}</span>
        <span>{t("heatmap.totalJobs", { count: total, defaultValue: `${total} jobs` })}</span>
      </div>

      <div
        className="grid gap-[3px]"
        style={{
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${rows}, 22px)`,
        }}
      >
        {data.map((value, idx) => {
          const bucket = bucketize(value, max);
          const opacity = BUCKET_OPACITY[bucket];
          const empty = bucket === 0;
          return (
            <div
              key={idx}
              className="rounded-[3px]"
              style={{
                backgroundColor: empty
                  ? "var(--color-muted)"
                  : `color-mix(in srgb, var(--color-primary) ${opacity * 100}%, transparent)`,
              }}
              title={`${value}`}
            />
          );
        })}
      </div>

      <div className="flex items-center justify-between text-[10px] text-faint">
        <span>{t("heatmap.legendLess", { defaultValue: "Less" })}</span>
        <div className="flex items-center gap-1">
          {BUCKET_OPACITY.map((opacity, i) => (
            <span
              key={i}
              className="h-2 w-2 rounded-[2px]"
              style={{
                backgroundColor:
                  i === 0
                    ? "var(--color-muted)"
                    : `color-mix(in srgb, var(--color-primary) ${opacity * 100}%, transparent)`,
              }}
            />
          ))}
          <span>{t("heatmap.legendMore", { defaultValue: "More" })}</span>
        </div>
        <span>{t("heatmap.today", { defaultValue: "Today" })}</span>
      </div>
    </div>
  );
}
